/**
 * 好友对战页面 — 完整版
 * 路由：/friend/:code
 * 音效、K线图、连胜系统全部与正式房一致
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLang, LangToggle } from '../i18n';
import { useParams, useNavigate } from 'react-router-dom';
import API, { createWS } from '../api';
import { useApp } from '../store';
import {
  initAudio, cleanupAudio,
  playSfx, playLoopSfx, playBgm, stopBgm,
} from '../audio';

/* ── 常量 ─────────────────────────────────────────────────────────── */
const CARRY       = 10000;
const GOLD        = '#F5C518';
const GREEN       = '#2ECC71';
const RED         = '#E8453C';
const PRICE_LIMIT = 400;

function friendHostKey(code) {
  return `friend_host_${String(code || '').toUpperCase()}`;
}

function friendSessionKey(code) {
  return `friend_session_${String(code || '').toUpperCase()}`;
}

function readFriendSession(code) {
  try {
    const raw = localStorage.getItem(friendSessionKey(code));
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (/^g[a-f0-9]{12}$/.test(s?.playerId || '') && /^[a-f0-9]{32}$/.test(s?.sessionToken || '')) return s;
  } catch {}
  return null;
}

function saveFriendSession(code, playerId, sessionToken) {
  if (!playerId || !sessionToken) return;
  localStorage.setItem(friendSessionKey(code), JSON.stringify({ playerId, sessionToken }));
}

/* ── SFX helpers（与 Game.jsx 完全一致）────────────────────────────── */
const SFX = {
  click:     ()  => playSfx('button-click', 0.9),
  open:      ()  => playSfx('gamble-flip', 0.9),
  profit:    ()  => playSfx('score-up', 0.75, 1.2),
  loss:      ()  => playSfx('short-circuit', 0.4, 1.35),
  jackpot:   ()  => playSfx('combo-win', 1.05),
  combo: (n = 1) => {
    const level = Math.max(1, Math.min(8, Number(n) || 1));
    playSfx(level >= 3 ? 'combo-win' : 'cluster-win',
            level >= 3 ? 1 : 0.8,
            Math.min(1.35, 0.92 + level * 0.05));
  },
  comboCash: ()  => playSfx('score-up', 0.85, 1.55),
  win:       ()  => playSfx('gamble-win', 0.9),
  lose:      ()  => playSfx('gamble-lose', 0.9),
  bust:      ()  => playSfx('bomb-explode', 1),
  settleWin: ()  => playSfx('settle-win', 1),
  settleLose: () => { playSfx('gamble-lose', 0.9); setTimeout(() => playSfx('bomb-explode', 1), 120); },
};

/* ── appendPricePoint（与 Game.jsx 一致）────────────────────────────── */
function appendPricePoint(prev, price, ts = Date.now()) {
  const p = Number(price);
  if (!Number.isFinite(p) || p <= 0) return prev;
  const last = prev[prev.length - 1];
  const nextTs = last && ts <= last.ts ? last.ts + 1 : ts;
  const next = [...prev, { ts: nextTs, price: p }];
  return next.length > PRICE_LIMIT ? next.slice(-PRICE_LIMIT) : next;
}

/* ══════════════════════════════════════════════════════════════════
   K线图 — 直接从 Game.jsx 移植，完整版
   ══════════════════════════════════════════════════════════════════ */
function CandleChart({ points, direction, markers }) {
  const canvasRef = useRef(null);
  const sizeRef   = useRef({ W: 0, H: 0 });
  const displayRef = useRef([]);
  const animRef   = useRef(null);

  const draw = useCallback((cv, data) => {
    const { W, H } = sizeRef.current;
    if (!W || !H || data.length < 2) return;
    const ctx = cv.getContext('2d');
    const slice = data.slice(-260);
    if (slice.length < 2) return;

    const bottomPad = 28, rightPad = 16;
    const plotRight = Math.max(40, W - rightPad);
    const toX = i => (slice.length === 1 ? 0 : i * plotRight / (slice.length - 1));
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
    const lerp  = (a, b, t) => a + (b - a) * t;

    const pointAtTs = ts => {
      if (ts <= slice[0].ts) return { price: slice[0].price, x: toX(0) };
      const li = slice.length - 1;
      if (ts >= slice[li].ts)  return { price: slice[li].price, x: toX(li) };
      let hi = slice.findIndex(p => p.ts >= ts);
      if (hi <= 0) hi = 1;
      const lo2 = hi - 1;
      const t = clamp((ts - slice[lo2].ts) / ((slice[hi].ts - slice[lo2].ts) || 1), 0, 1);
      return { price: lerp(slice[lo2].price, slice[hi].price, t), x: toX(lerp(lo2, hi, t)) };
    };

    const ss = slice[0].ts, se = slice[slice.length - 1].ts;
    const vm = (markers || [])
      .filter(m => m.ts && Number.isFinite(Number(m.price)) && m.ts >= ss && m.ts <= se)
      .map(m => ({ ...m, lp: pointAtTs(m.ts) }));

    const prices = slice.map(p => p.price).concat(vm.map(m => Number(m.price)));
    const minP = Math.min(...prices), maxP = Math.max(...prices);
    const pad  = (maxP - minP) * 0.12 || 1;
    const lo   = minP - pad, hi = maxP + pad;
    const toY  = p => (H - bottomPad - 8) * (1 - (p - lo) / (hi - lo)) + 4;

    ctx.fillStyle = '#0B1E4A';
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    for (let i = 1; i <= 3; i++) {
      const y = (H - bottomPad) * i / 4 + 4;
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
      const val = hi - ((hi - lo) * i / 4);
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px "Share Tech Mono",monospace';
      ctx.textAlign = 'right';
      ctx.fillText(val.toFixed(4), W - 3, y - 3);
    }

    const firstP = slice[0].price, lastP = slice[slice.length - 1].price;
    const isUp = lastP >= firstP;
    const lineCol = isUp ? GREEN : RED;
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    if (!isUp) {
      grad.addColorStop(0, 'rgba(232,69,60,0.30)');
      grad.addColorStop(0.62, 'rgba(232,69,60,0.05)');
      grad.addColorStop(1, 'rgba(232,69,60,0)');
    } else {
      grad.addColorStop(0, 'rgba(46,204,113,0.26)');
      grad.addColorStop(0.62, 'rgba(46,204,113,0.05)');
      grad.addColorStop(1, 'rgba(46,204,113,0)');
    }

    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, H - bottomPad);
    slice.forEach((p, i) => ctx.lineTo(toX(i), toY(p.price)));
    ctx.lineTo(plotRight, H - bottomPad);
    ctx.closePath(); ctx.fill();

    ctx.strokeStyle = lineCol;
    ctx.lineWidth = 2.4;
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.beginPath();
    slice.forEach((p, i) => { const x = toX(i), y = toY(p.price); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); });
    ctx.stroke();

    const last = slice[slice.length - 1];
    const lx = plotRight, ly = toY(last.price);
    ctx.strokeStyle = isUp ? 'rgba(46,204,113,0.36)' : 'rgba(232,69,60,0.36)';
    ctx.lineWidth = 1; ctx.setLineDash([3, 4]);
    ctx.beginPath(); ctx.moveTo(0, ly); ctx.lineTo(plotRight, ly); ctx.stroke();
    ctx.setLineDash([]);

    const pr = 9 + Math.sin(Date.now() / 140) * 2.2;
    ctx.beginPath(); ctx.arc(lx, ly, pr, 0, Math.PI * 2);
    ctx.fillStyle = isUp ? 'rgba(46,204,113,0.18)' : 'rgba(232,69,60,0.18)';
    ctx.fill();
    ctx.save(); ctx.shadowColor = lineCol; ctx.shadowBlur = 10;
    ctx.beginPath(); ctx.arc(lx, ly, 4.8, 0, Math.PI * 2);
    ctx.fillStyle = lineCol; ctx.fill(); ctx.restore();
    ctx.strokeStyle = '#0B1E4A'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(lx, ly, 4.8, 0, Math.PI * 2); ctx.stroke();

    if (vm.length > 0) {
      const placed = [];
      const clampRect = (x, y, w, h) => ({
        x: clamp(x, 4, W - w - 4),
        y: clamp(y, 4, H - h - bottomPad - 4),
        w, h,
      });
      const overlaps = (a, b) => (
        a.x < b.x + b.w + 7 && a.x + a.w + 7 > b.x &&
        a.y < b.y + b.h + 7 && a.y + a.h + 7 > b.y
      );
      for (const m of [...vm].sort((a, b) => a.ts - b.ts)) {
        const isBuy = m.dir === 1;
        const col = isBuy ? GREEN : RED;
        const mx = m.lp.x, my = toY(m.lp.price);
        const label = `${isBuy ? 'BUY' : 'SELL'}@${Number(m.price).toFixed(4)}`;
        const bw = Math.max(90, label.length * 7 + 18), bh = 24;
        const baseY = isBuy ? my - bh - 32 : my + 26;
        let rect = clampRect(mx - bw / 2, baseY, bw, bh);
        for (let d = 0; d < 4 && placed.some(p => overlaps(rect, p)); d++) {
          rect = clampRect(mx - bw / 2, isBuy ? baseY - (d + 1) * 32 : baseY + (d + 1) * 32, bw, bh);
        }
        placed.push(rect);
        const ax = clamp(mx, rect.x + 10, rect.x + rect.w - 10);
        const ey = isBuy ? rect.y + rect.h : rect.y;

        ctx.strokeStyle = col; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(mx, my); ctx.lineTo(ax, ey); ctx.stroke();
        ctx.fillStyle = col; ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(rect.x, rect.y, rect.w, rect.h, 6);
        else ctx.rect(rect.x, rect.y, rect.w, rect.h);
        ctx.fill();
        ctx.save(); ctx.shadowColor = col; ctx.shadowBlur = 8;
        ctx.fillStyle = col; ctx.beginPath(); ctx.arc(mx, my, 4.7, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
        ctx.strokeStyle = '#0B1E4A'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.arc(mx, my, 4.7, 0, Math.PI * 2); ctx.stroke();
        ctx.fillStyle = isBuy ? '#000' : '#fff';
        ctx.font = 'bold 10px "Share Tech Mono",monospace';
        ctx.textAlign = 'center';
        ctx.fillText(label, rect.x + rect.w / 2, rect.y + 16);
      }
    }

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 12px "Share Tech Mono",monospace';
    ctx.textAlign = 'left';
    ctx.fillText(new Date(slice[0].ts).toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit' }), 4, H - 8);
    ctx.textAlign = 'right';
    ctx.fillText(new Date(last.ts).toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit' }), W - 4, H - 8);
  }, [direction, markers]);

  const animateTo = useCallback(target => {
    const cv = canvasRef.current;
    if (!cv || target.length === 0) return;
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const start = displayRef.current.length > 0 ? displayRef.current : target;
    if (start.length === 0) { displayRef.current = target; draw(cv, target); return; }
    const byTs = new Map(start.map(p => [p.ts, p]));
    const last0 = start[start.length - 1];
    const lerp = (a, b, t) => a + (b - a) * t;
    const ease = t => 1 - Math.pow(1 - t, 3);
    const t0 = performance.now();
    const frame = now => {
      const k = ease(Math.min(1, (now - t0) / 280));
      displayRef.current = target.map((p, i) => {
        const from = byTs.get(p.ts) || { price: byTs.get(target[i - 1]?.ts)?.price ?? last0?.price ?? p.price };
        return { ...p, price: lerp(from.price, p.price, k) };
      });
      draw(cv, displayRef.current);
      if (k < 1) animRef.current = requestAnimationFrame(frame);
      else { displayRef.current = target; draw(cv, target); }
    };
    animRef.current = requestAnimationFrame(frame);
  }, [draw]);

  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const set = () => {
      const r = cv.getBoundingClientRect(), dpr = window.devicePixelRatio || 1;
      const W = Math.floor(r.width * dpr), H = Math.floor(r.height * dpr);
      if (!W || !H) return;
      cv.width = W; cv.height = H;
      cv.getContext('2d').scale(dpr, dpr);
      sizeRef.current = { W: r.width, H: r.height };
      draw(cv, displayRef.current.length > 0 ? displayRef.current : (cv._last || []));
    };
    set();
    const ro = new ResizeObserver(set); ro.observe(cv);
    return () => { ro.disconnect(); if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [draw]);

  useEffect(() => {
    if (canvasRef.current && points.length > 0) {
      const target = points.slice(-320).map(p => ({ ...p }));
      canvasRef.current._last = target;
      animateTo(target);
    }
  }, [points, animateTo]);

  return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

/* ══════════════════════════════════════════════════════════════════
   连胜仪表盘（与 Game.jsx 一致）
   ══════════════════════════════════════════════════════════════════ */
function ComboMeter({ streak = 0, best = 0, lang = 'zh' }) {
  const active = Math.max(0, Math.min(5, Number(streak) || 0));
  const bestV  = Math.max(active, Math.min(5, Number(best) || 0));
  return (
    <div className={`fg-combo-meter ${active > 0 ? 'is-live' : ''}`}>
      <div className="fg-combo-track">
        <span className="fg-combo-label">{lang==='en'?'STK':'连胜'}</span>
        {[1,2,3,4,5].map(n => (
          <span key={n} className={`fg-combo-cell ${n <= active ? 'is-lit' : ''} ${n <= active && active >= 3 ? 'is-critical' : ''} ${n === active && active > 0 ? 'is-hot' : ''}`}>
            x{n}
          </span>
        ))}
        {active === 5 && <span className="fg-combo-max">MAX</span>}
      </div>
      {bestV >= 3 && <span className="fg-combo-best">BEST x{bestV}</span>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   翻牌价格（与 Game.jsx 一致）
   ══════════════════════════════════════════════════════════════════ */
function FlipPrice({ value }) {
  const next = Number.isFinite(value) ? `$${value.toFixed(4)}` : '---';
  const [disp, setDisp] = useState(next);
  const [prev, setPrev] = useState(next);
  const [fid,  setFid]  = useState(0);
  useEffect(() => {
    if (next === disp) return;
    setPrev(disp); setDisp(next); setFid(id => id + 1);
  }, [next, disp]);
  const w = Math.max(disp.length, prev.length);
  const cur = disp.padStart(w, ' ');
  const old = prev.padStart(w, ' ');
  return (
    <div className="fg-price-big">
      {cur.split('').map((ch, i) => {
        const oc = old[i] || ' ';
        const changed = /\d/.test(ch) && oc !== ch;
        return (
          <span key={`${fid}-${i}`}
            className={['fg-pfc', changed ? 'is-flipping' : '', ch === '.' ? 'is-dot' : '', ch === '$' ? 'is-sym' : ''].filter(Boolean).join(' ')}>
            <span className="fg-pfc-static">{ch}</span>
            {changed && <>
              <span className="fg-pfc-face fg-pfc-old">{oc}</span>
              <span className="fg-pfc-face fg-pfc-new">{ch}</span>
            </>}
          </span>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   结算弹窗
   ══════════════════════════════════════════════════════════════════ */
function SettleModal({ results, carryPts, myId, onPlayAgain, onExit, canPlayAgain, lang = 'zh' }) {
  const cvRef = useRef(null);
  const myResult = results.find(r => r.playerId === myId);
  const won = myResult && !myResult.isBust && myResult.finalPts > carryPts;

  useEffect(() => {
    const cv = cvRef.current; if (!cv) return;
    const W = cv.width = window.innerWidth, H = cv.height = window.innerHeight;
    const ctx = cv.getContext('2d');
    let anim;
    if (won) {
      const coins = Array.from({ length: 60 }, () => ({
        x: Math.random() * W, y: -20 - Math.random() * 160,
        vy: 2 + Math.random() * 4, vx: (Math.random() - .5) * 3,
        r: 5 + Math.random() * 7, rot: Math.random() * Math.PI * 2,
        rotV: (Math.random() - .5) * 0.2, hue: 45 + Math.random() * 20, alpha: 1,
      }));
      let f = 0;
      const draw = () => {
        ctx.clearRect(0, 0, W, H); f++;
        for (const c of coins) {
          c.y += c.vy; c.x += c.vx; c.rot += c.rotV; c.vy += 0.12;
          if (f > 80) c.alpha = Math.max(0, c.alpha - 0.014);
          ctx.save(); ctx.globalAlpha = c.alpha;
          ctx.translate(c.x, c.y); ctx.rotate(c.rot);
          ctx.beginPath(); ctx.ellipse(0, 0, c.r, c.r * Math.abs(Math.cos(c.rot * 3)) + 1, 0, 0, Math.PI * 2);
          ctx.fillStyle = `hsl(${c.hue},90%,55%)`; ctx.fill();
          ctx.strokeStyle = `hsl(${c.hue},90%,35%)`; ctx.lineWidth = 1.5; ctx.stroke();
          ctx.restore();
        }
        if (f < 150) anim = requestAnimationFrame(draw);
      };
      draw();
    } else {
      const cx = W / 2, cy = H / 2;
      const sparks = Array.from({ length: 50 }, () => {
        const a = Math.random() * Math.PI * 2, spd = 4 + Math.random() * 9;
        return { x: cx, y: cy, vx: Math.cos(a) * spd, vy: Math.sin(a) * spd, r: 3 + Math.random() * 5, alpha: 1, color: Math.random() > .4 ? RED : '#FF8800' };
      });
      ctx.fillStyle = 'rgba(232,69,60,0.3)'; ctx.fillRect(0, 0, W, H);
      let f2 = 0;
      const draw2 = () => {
        ctx.clearRect(0, 0, W, H); f2++;
        for (const s of sparks) {
          s.x += s.vx; s.y += s.vy; s.vy += 0.3; s.alpha = Math.max(0, s.alpha - 0.022);
          ctx.save(); ctx.globalAlpha = s.alpha;
          ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = s.color; ctx.fill(); ctx.restore();
        }
        if (f2 < 80) anim = requestAnimationFrame(draw2);
      };
      draw2();
    }
    return () => cancelAnimationFrame(anim);
  }, [won]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px' }}>
      <canvas ref={cvRef} style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 201 }} />
      <div style={{ width: '100%', maxWidth: 430, background: '#0F2554', borderRadius: 20, padding: '28px 20px 22px', position: 'relative', zIndex: 202 }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 56, fontWeight: 900, color: won ? GOLD : RED, lineHeight: 1 }}>{won ? 'WIN' : 'OUT'}</div>
          <div style={{ fontSize: 12, color: '#4A6A9A', marginTop: 4 }}>{lang==='en'?'Friend Battle Result':'好友对战结果'}</div>
        </div>
        {results.map((r, i) => {
          const isMe = r.playerId === myId;
          const net  = r.isBust ? -carryPts : r.finalPts - carryPts;
          return (
            <div key={r.playerId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 12, background: isMe ? 'rgba(46,127,232,0.15)' : '#0B1E4A', marginBottom: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14, color: i < 3 ? '#000' : '#4A6A9A', background: i === 0 ? GOLD : i === 1 ? '#B0B8C1' : i === 2 ? '#CD7F32' : '#1a2d5a' }}>{i + 1}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: isMe ? '#7EB8FF' : '#fff' }}>{r.name}{isMe ? ` (${lang==='en'?'You':'你'})` : ''}</div>
                {r.isBust && <div style={{ fontSize: 11, color: RED }}>{lang==='en'?'Busted':'已爆仓'}</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 17, fontWeight: 900, color: r.isBust ? RED : net >= 0 ? GOLD : RED }}>
                  {r.isBust ? '0' : r.finalPts.toLocaleString()} PTS
                </div>
                <div style={{ fontSize: 11, color: net >= 0 ? GREEN : RED }}>{net >= 0 ? '+' : ''}{net.toLocaleString()}</div>
              </div>
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={onExit} style={{ flex: 1, padding: '12px 0', borderRadius: 12, border: 'none', background: '#1a2d5a', color: '#8BA8D4', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>{lang==='en'?'Exit':'退出'}</button>
          {canPlayAgain ? (
            <button onClick={onPlayAgain} style={{ flex: 2, padding: '12px 0', borderRadius: 12, border: 'none', background: `linear-gradient(180deg,${GOLD} 0%,#E6A800 100%)`, color: '#000', fontWeight: 900, cursor: 'pointer', fontSize: 14, boxShadow: '0 3px 0 #8B6200' }}>{lang==='en'?'Play Again':'再来一局'}</button>
          ) : (
            <div style={{ flex: 2, padding: '12px 0', borderRadius: 12, background: '#0B1E4A', color: '#8BA8D4', fontWeight: 700, fontSize: 13, textAlign: 'center' }}>{lang==='en'?'Waiting for host':'等待房主'}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   主组件
   ══════════════════════════════════════════════════════════════════ */
export default function FriendGame() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useApp();
  const { t, lang } = useLang();
  const codeUpper = (code || '').toUpperCase();

  /* 步骤状态 */
  const [step,       setStep]       = useState('name');   // name|connecting|waiting|playing|settled
  const [myName,     setMyName]     = useState('');
  const [myId,       setMyId]       = useState(null);
  const [isHost,     setIsHost]     = useState(false);
  const [players,    setPlayers]    = useState([]);
  const [error,      setError]      = useState('');

  /* 游戏状态 */
  const [gameStatus,    setGameStatus]    = useState('WAITING');
  const [countdown,     setCountdown]     = useState(5);
  const [remain,        setRemain]        = useState(60);
  const [currentPrice,  setCurrentPrice]  = useState(null);
  const [pricePoints,   setPricePoints]   = useState([]);
  const [direction,     setDirection]     = useState(null);  // 1=long -1=short null=flat
  const [openPrice,     setOpenPrice]     = useState(null);
  const [currentPts,    setCurrentPts]    = useState(CARRY);
  const [isBust,        setIsBust]        = useState(false);
  const [settled,       setSettled]       = useState(null);
  const [tradeLogs,     setTradeLogs]     = useState([]);
  const [comboPop,      setComboPop]      = useState(null);
  const [comboInfo,     setComboInfo]     = useState({ streak: 0, best: 0 });
  const [segSettle,     setSegSettle]     = useState(null);
  const [firstTip,      setFirstTip]      = useState(false);
  const segSettleTimer  = useRef(null);
  const [topNotice,     setTopNotice]     = useState(null);
  const [flashMsg,      setFlashMsg]      = useState(null);
  const [dangerMode,    setDangerMode]    = useState(false);
  const [switching,     setSwitching]     = useState(false);
  const [pendingDir,    setPendingDir]    = useState(null);
  const [bullets,       setBullets]       = useState([]);
  const [copied,        setCopied]        = useState(false);
  const [dcCountdown,   setDcCountdown]   = useState(0);   // 断线爆仓倒计时（秒），0=正常
  const dcTimerRef    = useRef(null);   // 前端倒计时 interval
  const reconnectRef  = useRef(null);   // 重连 setTimeout
  const reconnectNameRef = useRef('');  // 重连时用的 playerName

  /* refs */
  const ws             = useRef(null);
  const startedAtRef   = useRef(null);
  const stepRef        = useRef('name');
  const gameStatusRef  = useRef('WAITING');

  // ref-synced setters（供 onclose 闭包读取最新值）
  const setStepSync = useCallback(v => {
    stepRef.current = typeof v === 'function' ? v(stepRef.current) : v;
    setStep(v);
  }, []);
  const setGameStatusSync = useCallback(v => {
    gameStatusRef.current = typeof v === 'function' ? v(gameStatusRef.current) : v;
    setGameStatus(v);
  }, []);
  const timerRef       = useRef(null);
  const cdRef          = useRef(null);
  const hbLoopRef      = useRef(null);
  const cdSfxRef       = useRef(null);
  const noticeTimer    = useRef(null);
  const comboPopTimer  = useRef(null);
  const bulletId       = useRef(0);
  const logId          = useRef(0);
  const openPriceRef   = useRef(null);
  const openPtsRef     = useRef(CARRY);
  const directionRef   = useRef(null);
  const pendingDirRef  = useRef(null);
  const currentPtsRef  = useRef(CARRY);
  const pricePointTsRef= useRef(0);
  const lastPriceSfxRef= useRef({ price: null, ts: 0 });
  const bustPlayedRef  = useRef(false);
  const handleMsgRef   = useRef(null);
  const myIdRef        = useRef(null);

  /* ── helpers ─────────────────────────────────────────────────── */
  const pushBullet = useCallback((text, type = 'info') => {
    const id = bulletId.current++;
    setBullets(prev => [...prev.slice(-2), { id, text, type }]);
    setTimeout(() => setBullets(prev => prev.filter(b => b.id !== id)), 2800);
  }, []);

  const showNotice = useCallback((text, type = 'info', ms = 1800) => {
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    setTopNotice({ id: Date.now(), text, type });
    noticeTimer.current = setTimeout(() => setTopNotice(null), ms);
  }, []);

  const stopCdSfx = useCallback(() => {
    if (cdSfxRef.current) { cdSfxRef.current.stop(); cdSfxRef.current = null; }
  }, []);

  const addPricePoint = useCallback((p, ts = Date.now()) => {
    const price = Number(p);
    if (!Number.isFinite(price) || price <= 0) return null;
    const pt = ts <= pricePointTsRef.current ? pricePointTsRef.current + 1 : ts;
    pricePointTsRef.current = pt;
    setPricePoints(prev => appendPricePoint(prev, price, pt));
    return pt;
  }, []);

  const playPriceSfx = useCallback(nextPrice => {
    const prev = lastPriceSfxRef.current.price;
    lastPriceSfxRef.current.price = nextPrice;
    if (prev == null) return;
    const delta = nextPrice - prev;
    const minMove = Math.max(0.000001, Math.abs(prev) * 0.000002);
    if (Math.abs(delta) < minMove) return;
    const now = Date.now();
    if (now - lastPriceSfxRef.current.ts < 500) return;
    lastPriceSfxRef.current.ts = now;
    if (delta > 0) playSfx('score-up', 0.42, 1.35);
    else playSfx('short-circuit', 0.28, 1.8);
  }, []);

  /* ── audio init / cleanup ────────────────────────────────────── */
  useEffect(() => {
    initAudio();
    return () => {
      clearInterval(timerRef.current);
      clearInterval(cdRef.current);
      clearInterval(dcTimerRef.current);
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
      if (hbLoopRef.current) hbLoopRef.current.stop();
      stopCdSfx();
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
      if (comboPopTimer.current) clearTimeout(comboPopTimer.current);
      cleanupAudio();
    };
  }, [stopCdSfx]);

  /* ── dangerMode heartbeat ────────────────────────────────────── */
  useEffect(() => {
    const isDanger = currentPts >= CARRY * 2;
    setDangerMode(isDanger);
    if (isDanger) {
      if (!hbLoopRef.current) hbLoopRef.current = playLoopSfx('heart-stop-heartbeat', 0.7);
    } else {
      if (hbLoopRef.current) { hbLoopRef.current.stop(); hbLoopRef.current = null; }
    }
  }, [currentPts]);

  /* ── BGM ─────────────────────────────────────────────────────── */
  useEffect(() => {
    if (['playing'].includes(step) && gameStatus !== 'ENDED') playBgm('game-bgm');
    else stopBgm();
  }, [step, gameStatus]);

  /* ── 加载历史K线 ─────────────────────────────────────────────── */
  useEffect(() => {
    API.get('/api/game/kline-history?n=100').then(d => {
      if (d.bars?.length > 0) {
        const pts = d.bars.map(b => ({ ts: b.ts, price: b.close }));
        pricePointTsRef.current = pts[pts.length - 1]?.ts || 0;
        lastPriceSfxRef.current.price = pts[pts.length - 1]?.price ?? null;
        setPricePoints(pts);
      }
    }).catch(() => {});
  }, []);

  /* ── 已登录用户自动跳过输名字，直接连接 ─────────────────────── */
  useEffect(() => {
    if (!user) return;
    const name = user.nickname || `${lang==='en'?'Player':'玩家'}${user.id}`;
    setMyName(name);
    setStepSync('connecting');
    connectWS(name);
  }, [user]); // eslint-disable-line

  /* ── WS 消息处理 ─────────────────────────────────────────────── */
  const handleMsg = useCallback(msg => {
    const me = myIdRef.current;
    switch (msg.type) {

      case 'FRIEND_JOINED': {
        const state = msg.rejoinState || {};
        const status = state.status || msg.status || 'WAITING';
        const pState = state.playerState || null;
        myIdRef.current = msg.playerId;
        setMyId(msg.playerId);
        if (msg.sessionToken) saveFriendSession(codeUpper, msg.playerId, msg.sessionToken);
        setPlayers(msg.players || []);
        setGameStatusSync(status);
        setIsHost(Boolean(msg.isHost));

        if (pState) {
          const dir = pState.directionValue ?? (pState.direction === 'LONG' ? 1 : pState.direction === 'SHORT' ? -1 : null);
          const open = Number(pState.openPrice);
          const hasOpen = dir != null && Number.isFinite(open) && open > 0;
          const openPts = Number(pState.openPts ?? CARRY);
          const pts = Number(pState.currentPts ?? CARRY);

          directionRef.current = dir;
          openPriceRef.current = hasOpen ? open : null;
          openPtsRef.current = Number.isFinite(openPts) ? openPts : CARRY;
          currentPtsRef.current = Number.isFinite(pts) ? pts : CARRY;
          setDirection(dir);
          setOpenPrice(hasOpen ? open : null);
          setCurrentPts(currentPtsRef.current);
          setIsBust(Boolean(pState.isBust));
          bustPlayedRef.current = Boolean(pState.isBust);
          setComboInfo({ streak: Number(pState.comboStreak || 0), best: Number(pState.bestCombo || 0) });
        }
        const joinedPrice = Number(state.currentPrice);
        if (Number.isFinite(joinedPrice) && joinedPrice > 0) {
          setCurrentPrice(joinedPrice);
          lastPriceSfxRef.current.price = joinedPrice;
          addPricePoint(joinedPrice, Date.now());
        }
        setSwitching(false); setPendingDir(null); pendingDirRef.current = null;

        if (status === 'WAITING') {
          clearInterval(timerRef.current);
          clearInterval(cdRef.current);
          stopCdSfx();
          setRemain(60);
          setSettled(null);
          setStepSync('waiting');
        } else if (status === 'COUNTDOWN') {
          clearInterval(timerRef.current);
          stopCdSfx();
          const secs = Math.max(0, Number(state.countdownSeconds || 5));
          setRemain(60);
          setCountdown(secs);
          setStepSync('playing');
          cdSfxRef.current = playLoopSfx('ticking-clock', 0.72);
          clearInterval(cdRef.current);
          let n = secs;
          cdRef.current = setInterval(() => {
            n--; setCountdown(Math.max(0, n));
            if (n <= 0) { clearInterval(cdRef.current); stopCdSfx(); }
          }, 1000);
        } else if (status === 'RUNNING') {
          stopCdSfx();
          clearInterval(cdRef.current);
          const remaining = Math.max(0, Number(state.remainingSeconds ?? 60));
          startedAtRef.current = Number(state.startedAt) || (Date.now() - (60 - remaining) * 1000);
          setStepSync('playing');
          clearInterval(timerRef.current);
          const tick = () => {
            const el = Date.now() - startedAtRef.current;
            const r = Math.max(0, 60 - Math.floor(el / 1000));
            setRemain(r);
            if (r === 0) clearInterval(timerRef.current);
          };
          tick();
          timerRef.current = setInterval(tick, 500);
        } else if (status === 'ENDED') {
          clearInterval(timerRef.current);
          clearInterval(cdRef.current);
          stopCdSfx();
          stopBgm();
          setDangerMode(false);
          if (hbLoopRef.current) { hbLoopRef.current.stop(); hbLoopRef.current = null; }
          setStepSync('settled');
          setSettled({ results: state.results || [], carryPts: state.carryPts || msg.carryPts || CARRY });
        } else {
          setStepSync(status === 'WAITING' ? 'waiting' : 'playing');
        }
        break;
      }

      case 'FRIEND_PLAYER_LIST':
        setPlayers(msg.players || []);
        {
          const mine = (msg.players || []).find(p => p.playerId === myIdRef.current);
          if (mine) setIsHost(Boolean(mine.isHost));
        }
        // 再来一局：后端重置后切换到等待界面
        if (msg.reset) {
          setSettled(null);
          setDirection(null); directionRef.current = null;
          setOpenPrice(null); openPriceRef.current = null;
          setCurrentPts(CARRY); currentPtsRef.current = CARRY;
          setIsBust(false);
          setRemain(60);
          setGameStatusSync('WAITING');
          setPricePoints([]);
          pricePointTsRef.current = 0;
          setTradeLogs([]);
          setComboInfo({ streak: 0, best: 0 });
          bustPlayedRef.current = false;
          setCurrentPrice(null);
          setStepSync('waiting');
        }
        break;

      case 'FRIEND_COUNTDOWN':
        setGameStatusSync('COUNTDOWN');
        setStepSync('playing');
        setCountdown(msg.seconds || 5);
        stopCdSfx();
        cdSfxRef.current = playLoopSfx('ticking-clock', 0.72);
        clearInterval(cdRef.current);
        let n = msg.seconds || 5;
        cdRef.current = setInterval(() => {
          n--; setCountdown(n);
          if (n <= 0) { clearInterval(cdRef.current); stopCdSfx(); }
        }, 1000);
        break;

      case 'FRIEND_GAME_START':
        stopCdSfx();
        setStepSync('playing');
        setGameStatusSync('RUNNING');
        startedAtRef.current = msg.startedAt || Date.now();
        bustPlayedRef.current = false;
        setCurrentPts(CARRY); currentPtsRef.current = CARRY;
        setIsBust(false);
        setComboInfo({ streak: 0, best: 0 });
        // 清空K线数据，防止第二局接续第一局数据
        setPricePoints([]);
        setTradeLogs([]);
        pricePointTsRef.current = 0;
        if(!localStorage.getItem('combo_tip_shown')){
          setFirstTip(true);
          localStorage.setItem('combo_tip_shown','1');
          setTimeout(()=>setFirstTip(false), 4000);
        }
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
          const el = Date.now() - startedAtRef.current;
          const r = Math.max(0, 60 - Math.floor(el / 1000));
          setRemain(r);
          if (r === 5) pushBullet(lang==='en'?'Last 5 seconds!':'最后 5 秒！', 'warn');
          if (r === 0) clearInterval(timerRef.current);
        }, 500);
        break;

      case 'FRIEND_PRICE_UPDATE': {
        const p = Number(msg.price);
        if (!isNaN(p) && p > 0) {
          setCurrentPrice(p);
          playPriceSfx(p);
          // 用后端统一时间戳，保证所有客户端K线完全同步
          addPricePoint(p, msg.ts || Date.now());
        }
        setPlayers(msg.players || []);
        const meP = (msg.players || []).find(pl => pl.playerId === me);
        if (meP) {
          const pts = meP.isBust ? 0 : (meP.currentPts ?? CARRY);
          currentPtsRef.current = pts;
          setCurrentPts(pts);
        }
        break;
      }

      case 'FRIEND_PLAYER_BUST':
        if (msg.playerId === me) {
          setIsBust(true);
          if (hbLoopRef.current) { hbLoopRef.current.stop(); hbLoopRef.current = null; }
          setDangerMode(false);
          if (!bustPlayedRef.current) {
            bustPlayedRef.current = true;
            SFX.lose();
            setTimeout(() => SFX.bust(), 80);
          }
        } else {
          pushBullet(lang==='en'?`${msg.name || 'Opponent'} busted`:`${msg.name || '对手'} 已爆仓`, 'bust');
        }
        break;

      case 'FRIEND_POSITION_ACK': {
        if (!msg.ok) { setSwitching(false); setPendingDir(null); pendingDirRef.current = null; break; }
        const nOpen = Number(msg.openPrice), nPts = Number(msg.openPts ?? currentPtsRef.current);
        const nextDir = pendingDirRef.current?.nextDir ?? directionRef.current;
        const prevDir = pendingDirRef.current?.prevDir;
        const hadPrev = Boolean(openPriceRef.current != null && prevDir != null);
        const ts = Date.now();
        const markerTs = addPricePoint(nOpen, ts) || ts;
        setTradeLogs(prev => [...prev, { id: logId.current++, kind: 'entry', dir: nextDir, ts: markerTs, price: nOpen }].slice(-40));

        /* 连胜逻辑 */
        const segPnl   = Number(msg.segmentPnl ?? 0);
        const comboMul = Number(msg.comboMultiplier ?? msg.comboStreak ?? 0);
        const bonus    = Number(msg.comboBonusPts ?? 0);
        const bestC    = Number(msg.bestCombo ?? comboMul);

        if (hadPrev) {
          const pnlText = `${segPnl >= 0 ? '+' : ''}${Math.round(segPnl).toLocaleString()}`;
          // 切换结算卡片
          if (segSettleTimer.current) clearTimeout(segSettleTimer.current);
          setSegSettle({ pnl: Math.round(segPnl), bonus: Math.round(bonus), streak: comboMul, won: segPnl > 0 });
          segSettleTimer.current = setTimeout(() => setSegSettle(null), 2800);
          if (segPnl > 0) {
            SFX.combo(comboMul || 1);
            if (bonus > 0) setTimeout(() => SFX.comboCash(), Math.min(180 + comboMul * 30, 420));
            setComboInfo(prev => ({ streak: comboMul, best: Math.max(prev.best || 0, bestC, comboMul) }));
            if (comboPopTimer.current) clearTimeout(comboPopTimer.current);
            setComboPop({ id: Date.now(), multiplier: comboMul || 1, bonus });
            comboPopTimer.current = setTimeout(() => { setComboPop(null); comboPopTimer.current = null; }, comboMul >= 3 ? 1050 : 780);
            setFlashMsg({ text: pnlText, color: GOLD });
          } else if (segPnl < 0) {
            SFX.loss();
            setComboInfo(prev => ({ ...prev, streak: 0 }));
            setComboPop(null);
            setFlashMsg({ text: pnlText, color: RED });
          } else {
            setComboInfo(prev => ({ ...prev, streak: 0 }));
          }
          const comboText = segPnl > 0
            ? `x${comboMul || 1}${bonus > 0 ? ` ${lang==='en'?'Crit':'暴击'} +${Math.round(bonus).toLocaleString()}` : ''}`
            : (lang==='en'?'Streak Reset':'连胜重置');
          const fgSegLabel = lang==='en'?`Seg ${pnlText}`:`本段 ${pnlText}`;
          showNotice(`${fgSegLabel} · ${comboText} · ${nextDir === 1 ? t('longUp') : t('shortDown')}`, segPnl > 0 ? 'profit' : segPnl < 0 ? 'loss' : 'info', segPnl > 0 && comboMul >= 3 ? 2200 : 1800);
          setTimeout(() => setFlashMsg(null), 1400);
        } else {
          SFX.open();
          showNotice(lang==='en'?`${nextDir === 1 ? t('longUp') : t('shortDown')} opened`:`${nextDir === 1 ? t('longUp') : t('shortDown')}已开仓`, nextDir === 1 ? 'buy' : 'sell');
        }

        openPriceRef.current = nOpen;
        openPtsRef.current = nPts;
        directionRef.current = nextDir;
        currentPtsRef.current = nPts;
        pendingDirRef.current = null;
        setOpenPrice(nOpen); setCurrentPts(nPts); setDirection(nextDir);
        setSwitching(false); setPendingDir(null);
        break;
      }

      case 'FRIEND_CLOSE_ACK':
        if (msg.ok) {
          openPriceRef.current = null;
          directionRef.current = null;
          const pts = Number(msg.currentPts ?? currentPtsRef.current);
          currentPtsRef.current = pts;
          setDirection(null); setOpenPrice(null); setCurrentPts(pts);
        }
        break;

      case 'FRIEND_GAME_SETTLE':
        clearInterval(timerRef.current);
        stopBgm();
        if (hbLoopRef.current) { hbLoopRef.current.stop(); hbLoopRef.current = null; }
        setDangerMode(false);
        setGameStatusSync('ENDED');
        setStepSync('settled');
        setSettled({ results: msg.results, carryPts: msg.carryPts || CARRY });
        /* 结算音效 */
        const myR = (msg.results || []).find(r => r.playerId === me);
        if (myR && !myR.isBust) {
          if (myR.finalPts >= (msg.carryPts || CARRY) * 1.5) SFX.jackpot();
          else if (myR.finalPts >= (msg.carryPts || CARRY)) SFX.settleWin();
          else SFX.lose();
        } else if (bustPlayedRef.current) {
          SFX.lose();
        } else {
          SFX.settleLose();
        }
        break;

      case 'FRIEND_DISCONNECT_COUNTDOWN': {
        // 自己断线重连成功后服务器会发 FRIEND_RECONNECTED，这里只处理自己的倒计时
        if (msg.playerId !== me) break;
        const secs = msg.seconds || 10;
        setDcCountdown(secs);
        if (dcTimerRef.current) clearInterval(dcTimerRef.current);
        dcTimerRef.current = setInterval(() => {
          setDcCountdown(prev => {
            if (prev <= 1) { clearInterval(dcTimerRef.current); return 0; }
            return prev - 1;
          });
        }, 1000);
        break;
      }

      case 'FRIEND_RECONNECTED':
        if (msg.playerId !== me) break;
        clearInterval(dcTimerRef.current);
        setDcCountdown(0);
        break;

      case 'ERROR':
        setError(msg.msg || '操作失败'); setSwitching(false); setPendingDir(null); pendingDirRef.current = null;
        setTimeout(() => setError(''), 2500);
        break;

      default: break;
    }
  }, [addPricePoint, codeUpper, lang, playPriceSfx, pushBullet, setGameStatusSync, setStepSync, showNotice, stopCdSfx, t]);

  useEffect(() => { handleMsgRef.current = handleMsg; }, [handleMsg]);

  /* ── 建立 WS 连接 ────────────────────────────────────────────── */
  const connectWS = useCallback(playerName => {
    reconnectNameRef.current = playerName;
    // 清除旧重连计划
    if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }

    const conn = createWS();
    ws.current = conn;
    conn.onopen = () => {
      initAudio();
      const token = localStorage.getItem('token');
      if (token) conn.send(JSON.stringify({ type: 'AUTH', payload: { token } }));
      const session = !user ? readFriendSession(codeUpper) : null;
      const hostToken = localStorage.getItem(friendHostKey(codeUpper)) || '';
      const payload = { code: codeUpper, playerName, hostToken };
      if (session) {
        payload.playerId = session.playerId;
        payload.sessionToken = session.sessionToken;
      }
      conn.send(JSON.stringify({ type: 'FRIEND_JOIN', payload }));
    };
    conn.onmessage = e => {
      try { handleMsgRef.current?.(JSON.parse(e.data)); } catch {}
    };
    conn.onclose = () => {
      // 游戏进行中断线：3秒后自动重连（在10秒爆仓窗口内）
      const curStep = stepRef.current;
      const curStatus = gameStatusRef.current;
      if ((curStep === 'playing' || curStep === 'waiting' || curStep === 'connecting') &&
          curStatus !== 'ENDED') {
        reconnectRef.current = setTimeout(() => {
          reconnectRef.current = null;
          connectWS(reconnectNameRef.current);
        }, 3000);
      }
    };
  }, [codeUpper, user]);

  function send(type, payload = {}) {
    if (ws.current?.readyState === WebSocket.OPEN)
      ws.current.send(JSON.stringify({ type, payload }));
  }

  /* ── 进入房间 ────────────────────────────────────────────────── */
  function enterRoom() {
    const name = myName.trim().slice(0, 16);
    if (!name) { setError(lang==='en'?'Please enter a name':'请输入一个名字'); return; }
    setError('');
    setStepSync('connecting');
    connectWS(name);
  }

  /* ── 游戏操作 ────────────────────────────────────────────────── */
  function startGame() { SFX.click(); send('FRIEND_START'); }

  function switchDir(dir) {
    if (isBust || switching || dir === directionRef.current || gameStatus !== 'RUNNING') return;
    initAudio();
    SFX.click();
    pendingDirRef.current = { prevDir: directionRef.current, nextDir: dir };
    setSwitching(true); setPendingDir(dir);
    showNotice(lang==='en'?`${dir === 1 ? t('longUp') : t('shortDown')} submitting...`:`${dir === 1 ? t('longUp') : t('shortDown')}提交中...`, dir === 1 ? 'buy' : 'sell', 1200);
    send('FRIEND_OPEN', { direction: dir === 1 ? 'LONG' : 'SHORT' });
  }

  function playAgain() {
    if (!isHost) {
      showNotice(lang==='en'?'Only host can start next round':'只有房主可以再来一局', 'info');
      return;
    }
    // 清除断线重连状态
    if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    clearInterval(dcTimerRef.current);
    setDcCountdown(0);
    // 发 FRIEND_RESET，后端重置房间并广播 FRIEND_PLAYER_LIST(reset:true)
    // 前端状态由 FRIEND_PLAYER_LIST 回调统一重置，避免竞态
    send('FRIEND_RESET', { code: codeUpper });
  }

  /* ── 衍生值 ─────────────────────────────────────────────────── */
  const activeDir     = pendingDir ?? direction;
  const floatingPts   = (direction != null && !isBust) ? currentPts - openPtsRef.current : 0;
  const floatPositive = floatingPts >= 0;
  const ptsRatio      = Math.max(0, Math.min(100, currentPts / CARRY * 100));
  const ptsClass      = isBust ? 'is-danger'
    : currentPts >= CARRY ? 'is-profit'
    : currentPts <= CARRY * 0.2 ? 'is-danger' : 'is-loss';
  const heroPrice     = currentPrice ?? (pricePoints.length > 0 ? pricePoints[pricePoints.length - 1]?.price : null);

  /* ══════════════════════════════════════════════════════════════
     CSS
     ══════════════════════════════════════════════════════════════ */
  const S = `
    *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent;}
    .fg-root{display:flex;flex-direction:column;height:100vh;height:100dvh;background:#0B1E4A;overflow:hidden;}
    .fg-header{display:flex;align-items:center;gap:12px;padding:50px 16px 14px;flex-shrink:0;}
    .fg-back{width:36px;height:36px;border-radius:50%;background:#0F2554;border:none;color:#fff;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;}
    .fg-title{font-size:16px;font-weight:800;color:#fff;flex:1;text-align:center;}
    .fg-code{font-size:12px;color:#4A6A9A;background:#0F2554;padding:4px 10px;border-radius:8px;font-family:'Share Tech Mono',monospace;}
    .fg-chart-area{flex:1;min-height:0;position:relative;}
    .fg-price-hero{padding:0 16px 10px;flex-shrink:0;}
    .fg-price-big{font-size:32px;font-weight:900;color:#fff;display:flex;align-items:flex-end;gap:1px;perspective:360px;font-variant-numeric:tabular-nums;}
    .fg-pfc{position:relative;display:inline-flex;align-items:center;justify-content:center;min-width:0.58em;height:1.05em;transform-style:preserve-3d;}
    .fg-pfc.is-sym{min-width:0.46em;}.fg-pfc.is-dot{min-width:0.22em;}
    .fg-pfc-static{display:block;}.fg-pfc.is-flipping .fg-pfc-static{opacity:0;}
    .fg-pfc-face{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;backface-visibility:hidden;transform-origin:center;will-change:transform,opacity;}
    .fg-pfc-old{animation:pfcOld 0.28s cubic-bezier(0.2,0.7,0.2,1) both;}
    .fg-pfc-new{animation:pfcNew 0.28s cubic-bezier(0.2,0.7,0.2,1) both;}
    .fg-price-sub{display:flex;align-items:center;gap:8px;margin-top:5px;flex-wrap:wrap;}
    .fg-float-chip{padding:3px 10px;border-radius:50px;font-size:12px;font-weight:800;font-family:'Share Tech Mono',monospace;}
    .fg-score{margin-top:10px;border-radius:14px;padding:12px 14px;background:#0F2554;position:relative;overflow:hidden;}
    .fg-score.is-profit{box-shadow:0 0 32px ${GOLD}20;}
    .fg-score.is-loss{box-shadow:0 0 24px ${RED}18;}
    .fg-score.is-danger{animation:scoreDanger 0.72s ease-in-out infinite alternate;box-shadow:0 0 36px ${RED}38;}
    .fg-score-val{font-family:'Share Tech Mono',monospace;font-size:32px;font-weight:900;color:#fff;}
    .fg-score.is-profit .fg-score-val{color:${GOLD};}
    .fg-score.is-loss .fg-score-val, .fg-score.is-danger .fg-score-val{color:${RED};}
    .fg-score-bar{margin-top:8px;height:9px;border-radius:999px;background:#091840;overflow:hidden;}
    .fg-score-fill{height:100%;border-radius:999px;transition:width 0.25s;background:${GOLD};box-shadow:0 0 10px ${GOLD}66;}
    .fg-combo-meter{margin-top:8px;display:flex;align-items:center;gap:8px;}
    .fg-combo-track{display:flex;align-items:center;gap:5px;padding:5px 8px;border-radius:10px;background:linear-gradient(90deg,#0D2B6B,#152D6A,#0D2B6B);box-shadow:inset 0 1px 0 rgba(255,255,255,0.08),0 3px 10px rgba(0,0,0,0.3);}
    .fg-combo-meter.is-live .fg-combo-track{box-shadow:0 0 20px rgba(255,202,58,0.2),inset 0 1px 0 rgba(255,255,255,0.1);}
    .fg-combo-label{font-size:11px;color:#8BA8D4;font-weight:700;margin-right:2px;white-space:nowrap;}
    .fg-combo-cell{width:36px;height:26px;display:flex;align-items:center;justify-content:center;font-family:'Share Tech Mono',monospace;font-size:13px;font-style:italic;font-weight:900;border-radius:5px;transition:all 0.2s;background:rgba(255,255,255,0.05);color:rgba(255,255,255,0.2);border:1px solid rgba(255,255,255,0.06);}
    .fg-combo-cell.is-lit{background:rgba(255,215,0,0.12);color:rgba(255,215,0,0.6);border-color:rgba(255,215,0,0.25);}
    .fg-combo-cell.is-lit.is-critical{background:rgba(255,140,0,0.2);color:rgba(255,180,0,0.85);border-color:rgba(255,160,0,0.4);}
    .fg-combo-cell.is-hot{background:rgba(255,215,0,0.22);color:#FFD700;border-color:rgba(255,215,0,0.6);font-size:15px;animation:comboHop 0.5s cubic-bezier(0.2,1.4,0.35,1) both;box-shadow:0 0 10px rgba(255,215,0,0.45);}
    .fg-combo-cell.is-hot.is-critical{background:rgba(255,140,0,0.3);color:#FFA500;border-color:rgba(255,160,0,0.7);box-shadow:0 0 14px rgba(255,140,0,0.7);}
    .fg-combo-max{font-size:10px;font-weight:900;color:#FF8C00;padding:2px 5px;border-radius:4px;background:rgba(255,140,0,0.15);}
    .seg-settle{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2400;pointer-events:none;text-align:center;animation:segSettleIn 0.3s cubic-bezier(0.2,1.4,0.35,1) both;}
    .seg-settle-card{min-width:180px;padding:14px 20px;border-radius:16px;background:rgba(10,22,58,0.96);border:1px solid rgba(255,255,255,0.1);box-shadow:0 8px 32px rgba(0,0,0,0.6);}
    .seg-settle.won .seg-settle-card{border-color:rgba(255,215,0,0.35);box-shadow:0 8px 32px rgba(0,0,0,0.6),0 0 24px rgba(255,215,0,0.15);}
    .seg-settle.lost .seg-settle-card{border-color:rgba(232,69,60,0.35);}
    .seg-settle-pnl{font-family:'Share Tech Mono',monospace;font-size:32px;font-weight:900;line-height:1;}
    .seg-settle.won .seg-settle-pnl{color:#FFD700;}
    .seg-settle.lost .seg-settle-pnl{color:#FF3B30;}
    .seg-settle-bonus{font-size:13px;font-weight:700;color:#FFA500;margin-top:4px;}
    .seg-settle-streak{font-size:12px;color:#8BA8D4;margin-top:6px;}
    .seg-settle-reset{font-size:13px;font-weight:700;color:#FF3B30;margin-top:4px;}
    .first-tip{position:fixed;bottom:130px;left:50%;transform:translateX(-50%);z-index:2500;pointer-events:none;white-space:nowrap;background:rgba(10,22,58,0.95);border:1px solid rgba(46,127,232,0.4);border-radius:12px;padding:10px 18px;font-size:13px;color:#8BA8D4;font-weight:600;box-shadow:0 4px 20px rgba(0,0,0,0.5);animation:tipFadeIn 0.4s ease both,tipFadeOut 0.4s ease 3.6s both;}
    .first-tip b{color:#FFD700;}
    @keyframes segSettleIn{from{opacity:0;transform:translate(-50%,-50%) scale(0.7);}to{opacity:1;transform:translate(-50%,-50%) scale(1);}}
    @keyframes tipFadeIn{from{opacity:0;transform:translateX(-50%) translateY(8px);}to{opacity:1;transform:translateX(-50%) translateY(0);}}
    @keyframes tipFadeOut{from{opacity:1;}to{opacity:0;}}
    .fg-combo-best{font-family:'Share Tech Mono',monospace;font-size:11px;font-weight:900;color:#F5A623;padding:3px 7px;border-radius:8px;background:rgba(245,166,35,0.12);white-space:nowrap;}
    .combo-burst{position:fixed;top:50%;left:50%;z-index:2300;pointer-events:none;text-align:center;transform:translate(-50%,-50%);animation:comboBurst 0.78s cubic-bezier(0.14,1.15,0.28,1) both;}
    .combo-burst-main{font-family:'Share Tech Mono',monospace;font-size:72px;line-height:0.9;font-style:italic;font-weight:900;color:#FFE66B;text-shadow:0 0 12px rgba(255,230,107,0.95),0 0 36px rgba(255,128,0,0.7),0 5px 0 rgba(91,31,0,0.75);}
    .combo-burst-sub{margin-top:8px;font-family:'Share Tech Mono',monospace;font-size:20px;font-weight:900;color:${GOLD};text-shadow:0 0 18px ${GOLD}88;}
    .combo-burst.is-critical .combo-burst-main{font-size:88px;animation:comboCriticalGlow 0.22s ease-in-out 3 alternate;}
    .fg-bottom{flex-shrink:0;padding:10px 16px 28px;}
    .fg-trade-btns{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
    .fg-tbtn{height:60px;border-radius:12px;border:none;font-size:20px;font-weight:900;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;-webkit-tap-highlight-color:transparent;}
    .fg-tbtn span{font-size:11px;font-weight:500;opacity:0.75;}
    .fg-tbtn:active{transform:scale(0.96);}
    .fg-tbtn:disabled{opacity:0.35;cursor:not-allowed;}
    .fg-start-btn{width:100%;height:56px;border-radius:12px;border:none;cursor:pointer;font-size:16px;font-weight:900;background:linear-gradient(180deg,${GOLD} 0%,#E6A800 100%);color:#000;box-shadow:0 4px 0 #8B6200;}
    .fg-start-btn:active{transform:translateY(3px);box-shadow:0 1px 0 #8B6200;}
    .fg-start-btn:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none;}
    .trade-notice-layer{position:fixed;top:86px;left:50%;transform:translateX(-50%);z-index:1600;width:88%;max-width:380px;pointer-events:none;}
    .trade-notice{padding:6px 14px;border-radius:99px;font-size:14px;font-weight:800;text-align:center;white-space:nowrap;animation:noticeIn 0.22s ease;color:#fff;}
    .bullet-layer{position:fixed;top:100px;left:50%;transform:translateX(-50%);z-index:500;width:88%;max-width:360px;pointer-events:none;}
    .bullet{padding:7px 14px;border-radius:50px;margin-bottom:5px;font-size:12px;font-weight:600;text-align:center;animation:bIn 0.2s ease;}
    .flash-pop{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:2000;pointer-events:none;}
    .danger-vignette{position:fixed;inset:0;pointer-events:none;z-index:999;animation:dangerBreath 1s ease-in-out infinite;background:radial-gradient(ellipse at center,transparent 30%,rgba(232,69,60,0.35) 65%,rgba(232,69,60,0.75) 100%);}
    .fg-card{background:#0F2554;border-radius:16px;padding:16px;}
    .fg-label{font-size:11px;color:#4A6A9A;letter-spacing:1px;margin-bottom:8px;text-transform:uppercase;}
    .name-input{width:100%;padding:12px 16px;border-radius:12px;border:none;background:#0B1E4A;color:#fff;font-size:16px;font-weight:600;outline:none;}
    .name-input::placeholder{color:#4A6A9A;}
    .btn-primary{width:100%;padding:13px;border-radius:12px;border:none;cursor:pointer;font-size:15px;font-weight:900;background:linear-gradient(180deg,${GOLD} 0%,#E6A800 100%);color:#000;box-shadow:0 4px 0 #8B6200;}
    .btn-primary:active{transform:translateY(3px);box-shadow:0 1px 0 #8B6200;}
    .btn-primary:disabled{opacity:.4;cursor:not-allowed;transform:none;box-shadow:none;}
    .player-row{display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid rgba(46,127,232,0.08);}
    .player-row:last-child{border-bottom:none;}
    @keyframes pfcOld{from{opacity:1;transform:rotateX(0)}to{opacity:0;transform:rotateX(-84deg) translateY(-8%)}}
    @keyframes pfcNew{from{opacity:0;transform:rotateX(84deg) translateY(8%)}to{opacity:1;transform:rotateX(0)}}
    @keyframes comboHop{0%{transform:translateY(8px) skew(-8deg) scale(0.86)}58%{transform:translateY(-5px) skew(-8deg) scale(1.2)}100%{transform:translateY(0) skew(-8deg) scale(1)}}
    @keyframes comboBurst{0%{opacity:0;transform:translate(-50%,-45%) scale(0.58) rotate(-7deg)}16%{opacity:1;transform:translate(-50%,-50%) scale(1.22) rotate(3deg)}48%{opacity:1;transform:translate(-50%,-50%) scale(1) rotate(0)}100%{opacity:0;transform:translate(-50%,-62%) scale(0.92)}}
    @keyframes comboCriticalGlow{from{filter:brightness(1)}to{filter:brightness(1.45)}}
    @keyframes scoreDanger{from{filter:brightness(1)}to{filter:brightness(1.25)}}
    @keyframes dangerBreath{0%,100%{opacity:0}50%{opacity:1}}
    @keyframes noticeIn{from{opacity:0;transform:translateY(-12px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}
    @keyframes bIn{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
  `;

  /* ══════════════════════════════════════════════════════════════
     渲染
     ══════════════════════════════════════════════════════════════ */
  return (
    <div className="fg-root">
      <style>{S}</style>

      {/* 危险模式红晕 */}
      {dangerMode && gameStatus === 'RUNNING' && <div className="danger-vignette"/>}

      {/* 顶部通知 */}
      {topNotice && (
        <div className="trade-notice-layer">
          <div key={topNotice.id} className={`trade-notice ${topNotice.type}`}>{topNotice.text}</div>
        </div>
      )}

      {/* 弹幕 */}
      <div className="bullet-layer">
        {bullets.map(b => (
          <div key={b.id} className="bullet" style={{ background: b.type === 'bust' ? 'rgba(232,69,60,0.9)' : b.type === 'warn' ? 'rgba(245,166,35,0.9)' : 'rgba(30,30,50,0.95)', color: '#fff' }}>{b.text}</div>
        ))}
      </div>

      {/* 连胜爆发弹窗 */}
      {comboPop && !segSettle && (
        <div key={comboPop.id} className={`combo-burst ${comboPop.multiplier >= 3 ? 'is-critical' : ''}`}>
          <div className="combo-burst-main">x{comboPop.multiplier}</div>
          {comboPop.bonus > 0 && <div className="combo-burst-sub">+{Math.round(comboPop.bonus).toLocaleString()} PTS</div>}
        </div>
      )}

      {/* 切换结算卡片 */}
      {segSettle && (
        <div className={`seg-settle ${segSettle.won ? 'won' : 'lost'}`}>
          <div className="seg-settle-card">
            <div className="seg-settle-pnl">{segSettle.pnl >= 0 ? '+' : ''}{segSettle.pnl.toLocaleString()}</div>
            {segSettle.won && segSettle.bonus > 0 && (
              <div className="seg-settle-bonus">{t('streakBonus')} +{segSettle.bonus.toLocaleString()}</div>
            )}
            {segSettle.won && (
              <div className="seg-settle-streak">{lang==='en'?`Streak x${segSettle.streak} — keep going`:`连胜 x${segSettle.streak} — 继续保持不要切换`}</div>
            )}
            {!segSettle.won && (
              <div className="seg-settle-reset">{lang==='en'?'Streak broken — switch after profit to chain':'连胜中断 — 盈利时切换才能连胜'}</div>
            )}
          </div>
        </div>
      )}

      {/* 首次游戏提示 */}
      {firstTip && (
        <div className="first-tip">
          {lang==='en' ? <>Switch direction after profit to chain streaks, <b>up to x5 crit bonus</b></> : <>盈利后切换方向可叠加连胜，<b>最高 x5 暴击奖励</b></>}
        </div>
      )}

      {/* PnL 浮层 */}
      {flashMsg && !segSettle && (
        <div className="flash-pop">
          <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 46, fontWeight: 900, color: flashMsg.color, whiteSpace: 'nowrap', textAlign: 'center', textShadow: `0 0 30px ${flashMsg.color}88` }}>
            {flashMsg.text} PTS
          </div>
        </div>
      )}

      {/* ── Header ───────────────────────────────────────────────── */}
      <div className="fg-header">
        <button className="fg-back" onClick={() => nav('/lobby')}>&#8592;</button>
        <div className="fg-title">{t('friendBattleTitle')}</div>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <LangToggle />
          <div className="fg-code">{codeUpper}</div>
        </div>
      </div>

      {/* 断线爆仓倒计时警告 */}
      {dcCountdown > 0 && (
        <div style={{
          background: 'rgba(232,69,60,0.92)', color: '#fff',
          padding: '8px 16px', textAlign: 'center',
          fontSize: 13, fontWeight: 700, flexShrink: 0,
          animation: 'dcBlink 0.6s ease-in-out infinite alternate',
        }}>
          {lang==='en'?`Disconnected! Busting in ${dcCountdown}s, reconnecting...`:`网络断线！${dcCountdown} 秒后爆仓，正在重连...`}
        </div>
      )}
      <style>{`
        @keyframes dcBlink {
          from { opacity: 1; }
          to   { opacity: 0.65; }
        }
      `}</style>

      {/* ── 步骤1：输入名字 ──────────────────────────────────────── */}
      {(step === 'name' || step === 'connecting') && (
        <div style={{ flex: 1, padding: '0 16px 32px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {error && <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(232,69,60,0.12)', color: RED, fontSize: 13, fontWeight: 600 }}>{error}</div>}
          <div className="fg-card">
            <div className="fg-label">{t('yourName')}</div>
            {user ? (
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', padding: '8px 0' }}>
                {user.nickname || `玩家${user.id}`}
                <span style={{ fontSize: 12, color: '#4A6A9A', marginLeft: 8 }}>（已登录账号）</span>
              </div>
            ) : (
              <input className="name-input" placeholder={lang==='en'?'Max 16 chars':'最多16字'} maxLength={16} value={myName}
                onChange={e => setMyName(e.target.value)} onKeyDown={e => e.key === 'Enter' && enterRoom()} autoFocus />
            )}
            <div style={{ height: 10 }} />
            <button className="btn-primary" disabled={step === 'connecting' || (!user && !myName.trim())} onClick={enterRoom}>
              {step === 'connecting' ? (lang==='en'?'Connecting...':'连接中...') : (lang==='en'?'Enter Room':'进入房间')}
            </button>
          </div>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: '#0F2554', fontSize: 12, color: '#4A6A9A', lineHeight: 1.7 }}>
            {lang==='en'?`No room cards or COIN needed. Each player starts with ${CARRY.toLocaleString()} PTS. Pure skill.`:`好友对战不消耗房卡或 COIN，每人默认 ${CARRY.toLocaleString()} PTS，纯粹比拼交易技巧。`}
          </div>
        </div>
      )}

      {/* ── 步骤2：等待室 ────────────────────────────────────────── */}
      {step === 'waiting' && (
        <div style={{ flex: 1, padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
          {error && <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(232,69,60,0.12)', color: RED, fontSize: 13, fontWeight: 600 }}>{error}</div>}

          <div className="fg-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{lang==='en'?'Friend Room':'好友对战房'}</div>
                <div style={{ fontSize: 12, color: '#4A6A9A', marginTop: 2 }}>{players.length} {lang==='en'?'joined':'人已加入'}</div>
              </div>
              <button onClick={() => {
                navigator.clipboard?.writeText(`${window.location.origin}/friend/${codeUpper}`).then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
              }} style={{
                padding: '7px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                background: copied ? 'rgba(46,204,113,0.2)' : 'rgba(46,127,232,0.2)',
                color: copied ? GREEN : '#2E7FE8',
                transition: 'background 0.2s, color 0.2s',
              }}>
                {copied ? t('linkCopied') : t('copyLink')}
              </button>
            </div>
            <div style={{ fontSize: 12, color: '#4A6A9A', wordBreak: 'break-all', background: '#0B1E4A', borderRadius: 8, padding: '7px 12px' }}>{window.location.origin}/friend/{codeUpper}</div>
          </div>

          <div className="fg-card">
            <div className="fg-label">{lang==='en'?'Players':'玩家列表'}</div>
            {players.length === 0
              ? <div style={{ textAlign: 'center', color: '#4A6A9A', padding: '14px 0', fontSize: 13 }}>{lang==='en'?'Waiting for friends...':'等待好友加入...'}</div>
              : players.map((p, i) => (
                <div key={p.playerId} className="player-row">
                  <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, background: i === 0 ? 'rgba(245,197,24,0.15)' : '#0B1E4A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: i === 0 ? GOLD : '#4A6A9A', fontWeight: 700 }}>{i + 1}</div>
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#fff' }}>
                    {p.name}
                    {p.playerId === myId && <span style={{ fontSize: 11, color: '#2E7FE8', marginLeft: 6 }}>({lang==='en'?'You':'你'})</span>}
                    {p.isHost && <span style={{ fontSize: 11, color: GOLD, marginLeft: 6 }}>{lang==='en'?'Host':'房主'}</span>}
                  </div>
                </div>
              ))}
          </div>

          {isHost
            ? <button className="fg-start-btn" disabled={players.length < 2} onClick={startGame}>{lang==='en'?`Start (${players.length} players)`:`开始游戏（${players.length} 人）`}</button>
            : <div style={{ textAlign: 'center', color: '#4A6A9A', fontSize: 13, padding: '8px 0' }}>{t('waitHost')}</div>}
        </div>
      )}

      {/* ── 步骤3：游戏中 ────────────────────────────────────────── */}
      {step === 'playing' && gameStatus !== 'ENDED' && (
        <>
          {/* 倒计时遮罩 */}
          {gameStatus === 'COUNTDOWN' && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.7)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, pointerEvents: 'none' }}>
              <div style={{ fontSize: 13, color: '#8BA8D4', letterSpacing: 2 }}>游戏即将开始</div>
              <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 80, fontWeight: 900, color: GOLD }}>{countdown}</div>
            </div>
          )}

          {/* 价格 */}
          <div className="fg-price-hero">
            <FlipPrice value={heroPrice} />
            <div className="fg-price-sub">
              {direction != null && openPrice != null ? (
                <>
                  <span className="fg-float-chip" style={{ background: floatPositive ? 'rgba(46,204,113,0.1)' : 'rgba(232,69,60,0.1)', color: floatPositive ? GOLD : RED }}>
                    {lang==='en'?'PnL':(floatPositive ? '浮盈' : '浮亏')} {floatingPts >= 0 ? '+' : ''}{Math.round(floatingPts).toLocaleString()} PTS
                  </span>
                  <span style={{ fontSize: 12, color: '#8BA8D4' }}>{lang==='en'?'Entry':'建仓'} ${openPrice.toFixed(4)}</span>
                </>
              ) : (
                <span style={{ fontSize: 14, color: '#8BA8D4', fontWeight: 700 }}>{players.length} {lang==='en'?'players in battle':'人对战中'}</span>
              )}
              <span style={{ marginLeft: 'auto', fontFamily: "'Share Tech Mono',monospace", fontSize: 16, fontWeight: 900, color: remain <= 10 ? RED : GOLD }}>{remain}s</span>
            </div>

            {/* PTS 仪表盘 */}
            {(gameStatus === 'RUNNING' || isBust) && (
              <div className={`fg-score ${isBust ? 'is-danger' : ptsClass}`} style={{ marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: '#8BA8D4', fontWeight: 700 }}>{lang==='en'?'Round PTS':'局内 PTS'}</span>
                  {direction != null && !isBust && (
                    <span style={{ fontSize: 12, color: floatPositive ? GREEN : RED, fontWeight: 600 }}>
                      {floatPositive ? '+' : ''}{Math.round(floatingPts).toLocaleString()} {lang==='en'?'PnL':(floatPositive ? '浮盈' : '浮亏')}
                    </span>
                  )}
                </div>
                <div className="fg-score-val">{isBust ? '0' : currentPts.toLocaleString()} PTS</div>
                <div className="fg-score-bar">
                  <div className="fg-score-fill" style={{ width: `${isBust ? 0 : ptsRatio}%`, background: (isBust || ptsClass === 'is-danger' || ptsClass === 'is-loss') ? RED : GOLD }} />
                </div>
                {isBust && <div style={{ marginTop: 6, fontSize: 13, color: RED, fontWeight: 700 }}>{t('bustWaiting')}</div>}
              </div>
            )}

            {/* 连胜仪表 */}
            {gameStatus === 'RUNNING' && !isBust && <ComboMeter streak={comboInfo.streak} best={comboInfo.best} lang={lang} />}
          </div>

          {/* K线图 */}
          <div className="fg-chart-area">
            {pricePoints.length > 0
              ? <CandleChart points={pricePoints} direction={direction} markers={tradeLogs} />
              : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4A6A9A', fontSize: 13 }}>加载行情中...</div>}
            {/* 倒计时水印 */}
            {gameStatus === 'RUNNING' && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '28vw', fontWeight: 900, color: '#fff', opacity: 0.06, lineHeight: 1, userSelect: 'none' }}>{String(remain).padStart(2, '0')}</div>
              </div>
            )}
          </div>

          {/* 对手实时面板 */}
          <div style={{ padding: '6px 16px 0', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
              {players.filter(p => p.playerId !== myId).map(p => (
                <div key={p.playerId} style={{ flexShrink: 0, padding: '5px 10px', borderRadius: 8, background: '#0F2554', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#8BA8D4', fontWeight: 600 }}>{p.name}</span>
                  <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 13, fontWeight: 700, color: p.isBust ? RED : p.currentPts >= CARRY ? GOLD : '#8BA8D4' }}>
                    {p.isBust ? 'BUST' : (p.currentPts || CARRY).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* 操作按钮 */}
          <div className="fg-bottom">
            {!isBust && gameStatus === 'RUNNING' && (
              <div className="fg-trade-btns">
                <button className="fg-tbtn"
                  onClick={() => switchDir(-1)} disabled={switching}
                  style={{ background: 'linear-gradient(180deg,#F05349 0%,#C0312A 100%)', color: '#fff', boxShadow: activeDir === -1 ? '0 4px 0 #7A1A16,0 6px 12px rgba(0,0,0,0.4)' : '0 2px 0 #7A1A16', opacity: activeDir === 1 ? 0.55 : 1, transform: activeDir === -1 ? 'translateY(0)' : 'translateY(2px)' }}>
                  <span style={{fontSize:19,fontWeight:900,opacity:1}}>{switching && pendingDir === -1 ? t('submitting') : activeDir === -1 ? t('shortActive') : activeDir === 1 ? t('switchToShort') : t('shortDown')}</span>
                  <span style={{fontSize:11,opacity:0.6,fontWeight:500}}>SELL</span>
                </button>
                <button className="fg-tbtn"
                  onClick={() => switchDir(1)} disabled={switching}
                  style={{ background: 'linear-gradient(180deg,#36D97A 0%,#1FA855 100%)', color: '#fff', boxShadow: activeDir === 1 ? '0 4px 0 #0F6B30,0 6px 12px rgba(0,0,0,0.4)' : '0 2px 0 #0F6B30', opacity: activeDir === -1 ? 0.55 : 1, transform: activeDir === 1 ? 'translateY(0)' : 'translateY(2px)' }}>
                  <span style={{fontSize:19,fontWeight:900,opacity:1}}>{switching && pendingDir === 1 ? t('submitting') : activeDir === 1 ? t('longActive') : activeDir === -1 ? t('switchToLong') : t('longUp')}</span>
                  <span style={{fontSize:11,opacity:0.6,fontWeight:500}}>BUY</span>
                </button>
              </div>
            )}
            {isBust && (
              <div style={{ textAlign: 'center', padding: '14px 0' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: RED }}>BUST</div>
                <div style={{ fontSize: 13, color: '#8BA8D4', marginTop: 4 }}>{t('bustWaiting')}</div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── 步骤4：结算 ──────────────────────────────────────────── */}
      {step === 'settled' && settled && (
        <SettleModal
          results={settled.results}
          carryPts={settled.carryPts}
          myId={myId}
          onPlayAgain={playAgain}
          onExit={() => nav('/lobby')}
          canPlayAgain={isHost}
          lang={lang}
        />
      )}
    </div>
  );
}
